import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { MessageService } from 'primeng/api';
import { BehaviorSubject, catchError, Observable, take } from 'rxjs';
import { ServiceBase } from './servicebase.service';

@Injectable({
  providedIn: 'root'
})
export class LocationService extends ServiceBase {
  addressLECasesSubj: BehaviorSubject<ServerModels.AddressLECases[]> = new BehaviorSubject([]);
  addressLECases$: Observable<ServerModels.AddressLECases[]> = this.addressLECasesSubj.asObservable();

  locationTypesSubj: BehaviorSubject<ServerModels.ENESS_REF_LocationTypes[]> = new BehaviorSubject([]);
  locationTypes$: Observable<ServerModels.ENESS_REF_LocationTypes[]> = this.locationTypesSubj.asObservable();

  addressPeopleSubj: BehaviorSubject<ServerModels.AddressPeople[]> = new BehaviorSubject([]);
  addressPeople$: Observable<ServerModels.AddressPeople[]> = this.addressPeopleSubj.asObservable();

  constructor(protected http: HttpClient,
    public messageService: MessageService) {
    super(http, messageService);
    this.getLocationTypes();
  }

  public addOrUpdateAddressStaging(addressDto: ServerModels.ENESS_AddressDTO): Observable<ServerModels.ENESS_AddressDTO> {
    return this.http.post<ServerModels.ENESS_AddressDTO>(`${this.apiRoot}/api/Address/AddOrUpdateAddressStaging`, addressDto, this.httpOptions)
      .pipe(catchError(this.throwError<any>('addOrUpdateAddressStaging', 'defaultMsg', null, null)));
  }

  public findAddressCandidates(address: string, key: string): Observable<string> {
      return this.http.put<string>(`${this.apiRoot}/api/Address/FindAddressCandidates`, { 'searchString': address, 'key': key }, this.httpOptions)
      .pipe(catchError(this.swallowError<any>('findAddressCandidates', 'defaultMsg', null, null)));
  }

  public findAddressSuggestions(address: string): Observable<string> {
    let options = {
      headers: this.httpOptions.headers,
      params: {
        'searchString': address
      }
    }
    return this.http.get<string>(`${this.apiRoot}/api/Address/FindAddressSuggestions`, options)
      .pipe(catchError(this.swallowError<any>('findAddressSuggestions', 'defaultMsg', null, null)));
  }

  public getAddressByReverseGeocode(latitude: string, longitude: string): Observable<string> {
    let options = {
      headers: this.httpOptions.headers,
      params: {
        'latitude': latitude,
        'longitude': longitude
      }
    }
    return this.http.get<string>(`${this.apiRoot}/api/Address/GetAddressByReverseGeocode`, options)
      .pipe(catchError(this.throwError<any>('getAddressByReverseGeocode', 'defaultMsg', null, null)));
  }

  public searchAddresses(streetAddress: string, unit: string, cityName: string, state: string, postalCode: string, postalCodeExtended: string): Observable<ServerModels.ENESS_AddressDTO[]> {
    let options = {
      headers: this.httpOptions.headers,
      params: {
        'streetAddress': streetAddress,
        'unit': unit,
        'city': cityName,
        'state': state,
        'postalCode': postalCode,
        'postalCodeExtended': postalCodeExtended
      }
    }

    return this.http.get<ServerModels.ENESS_Address[]>(`${this.apiRoot}/api/Address/SearchAddresses`, options)
      .pipe(catchError(this.throwError<any>('searchAddresses', 'defaultMsg', null, null))); 
  }

  public addOrUpdateLocation(location: ServerModels.ENESS_AddressDTO): Observable<string> {
    return this.http.post<string>(`${this.apiRoot}/api/Address/AddOrUpdateAddressAsync`, location, this.httpOptions)
      .pipe(catchError(this.throwError<any>('addLocation', 'defaultMsg', null, null))); 
  }

  public getAddressLECases(addressId: string): Observable<ServerModels.AddressLECases[]> {
    let headersObj = this.httpOptions.headers.append('Skip-Loader', 'true');
    let options = {
      params: {
        'addressId': addressId
      },
      headers: headersObj
    };
    return this.http.get<ServerModels.AddressLECases[]>(`${this.apiRoot}/api/Address/GetAddressLECases`, options)
      .pipe(catchError(this.swallowError<any>('getAddressLECases', 'defaultMsg', null, null))); 
  }

  public getAddressPeople(addressId: string): Observable<ServerModels.AddressPeople[]> {
    let headersObj = this.httpOptions.headers.append('Skip-Loader', 'true');
    let options = {
      params: {
        'addressId': addressId
      },
      headers: headersObj
    };
    return this.http.get<ServerModels.AddressPeople[]>(`${this.apiRoot}/api/Address/GetAddressPeople`, options)
      .pipe(catchError(this.swallowError<any>('getAddressPeople', 'defaultMsg', null, null))); 
  }

  public getAddressDetailsById(addressId: string): Observable<ServerModels.ENESS_AddressDTO> {
    let options = {
      params: {
        'addressId': addressId
      },
      headers: this.httpOptions.headers
    };
    return this.http.get<ServerModels.ENESS_AddressDTO>(`${this.apiRoot}/api/Address/GetAddressDetailsByID`, options)
      .pipe(catchError(this.swallowError<any>('getAddressDetailsById', 'defaultMsg', null, null))); 
  }

  public getAddressDetailsStagingById(addressStagingId: string): Observable<ServerModels.ENESS_AddressDTO> {
    let options = {
      params: {
        'addressStagingId': addressStagingId
      },
      headers: this.httpOptions.headers
    };
    return this.http.get<ServerModels.ENESS_AddressDTO>(`${this.apiRoot}/api/Address/GetAddressDetailsStagingByID`, options)
      .pipe(catchError(this.throwError<any>('getAddressDetailsStagingById', 'defaultMsg', null, null)));
  }

  public deleteAddress(data: ServerModels.ENESS_DeleteHistory): Observable<ServerModels.ENESS_Address> {
    return this.http.post<ServerModels.ENESS_Address>(`${this.apiRoot}/api/Address/DeleteLocation`, data, this.httpOptions)
      .pipe(catchError(this.swallowError<any>('deleteAddress', 'defaultMsg', null, null))); 
  }

  public hasDeletePermission(addressId: string): Observable<boolean> {
    let options = {
      headers: this.httpOptions.headers,
      params: {
        'addressId': addressId
      }
    }
    return this.http.get<boolean>(`${this.apiRoot}/api/Address/HasDeletePermission`, options)
      .pipe(catchError(this.swallowError<any>('HasDeletePermission', 'defaultMsg', null, null)));
  }

  public getAllAddresses(filters: ServerModels.FiltersViewModel): Observable<ServerModels.AddressViewModel[]> {
    let headersObj = this.httpOptions.headers.append('Skip-Loader', 'true');
    let options = {
      headers: headersObj
    };

    return this.http.post<ServerModels.AddressViewModel[]>(`${this.apiRoot}/api/Address/AllAddressesViewModelPaged?`, filters, options)
      .pipe(catchError(this.throwError<any>('getAllAddresses', null, null, null))); 
  }

  public getLocationTypes() {
    this.http.get<ServerModels.ENESS_REF_LocationTypes[]>(`${this.apiRoot}/api/Address/GetAllLocationTypes?`, this.httpOptions).pipe(
      take(1),
      catchError(this.swallowError<any>('getLocationTypes', 'defaultMsg', null, null))
    ).subscribe((results: ServerModels.ENESS_REF_LocationTypes[]) => this.locationTypesSubj.next(results));
  }

  public createEmptyAddressDTO(): ServerModels.ENESS_AddressDTO {
    const emptyAddressDto: ServerModels.ENESS_AddressDTO = <ServerModels.ENESS_AddressDTO>{
      AddressStagingID: null,
      AddressID: null,
      LocationTypeID: '',
      StreetAddress: null,
      Address1: null,
      Address2: null,
      PostalCode: null,
      PostalCodeExtended: null,
      CityName: null,
      CountyID: '',
      StateID: '',
      StateName: null,
      StateAbbreviation: null,
      CountryID: null,
      PoliceDepartment: null,
      PoliceDepartmentSubArea1: null,
      PoliceDepartmentSubArea2: null,
      Unit: null,
      CapturedLatitude: null,
      CapturedLongitude: null,
      EnteredLatitude: null,
      EnteredLongitude: null,
      ConsolidatedAddressReturned: null,
      ConfidenceScore: null,
      AnalystNotes: null,
    };

    return emptyAddressDto;
  }

  public getCrimeGunId(crimeGunNumber: string): Observable<string> {
    let options = {
      headers: this.httpOptions.headers,
      params: {
        'crimeGunNumber': crimeGunNumber
      }
    };

    return this.http.get<string>(`${this.apiRoot}/api/Person/GetCrimeGunId`, options)
      .pipe(catchError(this.swallowError<any>('getCrimeGunId', 'defaultMsg', null, null))); 
  }
}
